import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Confirmation gate for a BULK delete — an irreversible, destructive action. The
 * user must (a) see exactly how many credentials will be permanently deleted and
 * (b) type a random confirmation word, so a bulk delete can never happen from a
 * single stray click. Single-row delete keeps its lighter confirm.
 */
export function BulkDeleteModal({ count, busy, onConfirm, onClose }: {
  count: number;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('dashboard');
  // A fresh random word per open — nothing to muscle-memory or paste from before.
  const word = useMemo(() => {
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // no ambiguous I/L/O/0/1
    let w = '';
    for (let i = 0; i < 6; i++) w += alphabet[Math.floor(Math.random() * alphabet.length)];
    return w;
  }, []);
  const [typed, setTyped] = useState('');
  const matches = typed.trim().toUpperCase() === word;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <span aria-hidden className="flex-none text-2xl">🗑️</span>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">
              {t('awards.bulkDeleteTitle', 'Delete {{count}} credentials?', { count })}
            </h2>
            <p className="mt-1.5 text-sm text-gray-600">
              {t('awards.bulkDeleteBody', 'This permanently deletes {{count}} credentials and their public verification pages. It cannot be undone and does not refund credits. To keep an audit trail, use Revoke instead.', { count })}
            </p>
          </div>
        </div>

        <label className="mt-4 block text-sm font-medium text-gray-700">
          {t('awards.bulkDeleteTypeWord', 'Type')} <code className="mx-1 select-all rounded bg-gray-900 px-2 py-0.5 font-mono text-sm tracking-widest text-white">{word}</code> {t('awards.bulkDeleteToConfirm', 'to confirm')}
        </label>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={word}
          disabled={busy}
          className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 font-mono uppercase tracking-widest focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
        />

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches || busy}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600">
            {busy ? t('awards.bulkDeleteBusy', 'Deleting…') : t('awards.bulkDeleteConfirm', 'Delete {{count}}', { count })}
          </button>
        </div>
      </div>
    </div>
  );
}
